import { AfterViewInit, Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { NotifyOrConfirmType } from '../../enums/notify-or-confirm-type.enum';
import { NotifyOrConfirmInterface } from '../../interfaces/notify-or-confirm.interface';
import { NotifyOrConfirmService } from '../../services/notify-or-confirm.service';
import { SessionService } from '../../services/session.service';
import { LanguageService } from '../../services/language.service';

/**
 * Component to show notifications or confirms
 *
 * Works together with the NotifyOrConfirm Service
 *
 * see https://ng-bootstrap.github.io/#/components/modal
 */
@Component({
	selector: 'cfe-notify-or-confirm',
	templateUrl: './notify-or-confirm.component.html',
	styleUrls: ['./notify-or-confirm.component.scss']
})
export class NotifyOrConfirmComponent implements OnInit, AfterViewInit {
	// -----------------------------------------------------------------------------------------------------------------
	// Class variables
	// -----------------------------------------------------------------------------------------------------------------

	public config: NotifyOrConfirmInterface;
	public defaultConfig: NotifyOrConfirmInterface;
	protected configFields = ['title', 'message', 'type', 'buttonText', 'buttons'];
	public notifyOrConfirmType = NotifyOrConfirmType;
	public modal: NgbModalRef;

	// -----------------------------------------------------------------------------------------------------------------
	// View children
	// -----------------------------------------------------------------------------------------------------------------

	@ViewChild('notifyOrConfirmModal', { static: true }) modalElementRef: ElementRef;

	// -----------------------------------------------------------------------------------------------------------------
	// Constructor
	// -----------------------------------------------------------------------------------------------------------------

	/**
	 * Init services
	 */
	constructor(
		private modalService: NgbModal,
		private notifyOrConfirmService: NotifyOrConfirmService,
		private ngZone: NgZone,
		private translateService: TranslateService,
		private languageService: LanguageService
	) {}

	// -----------------------------------------------------------------------------------------------------------------
	// Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------------------

	/**
	 * Lifecycle hook OnInit
	 */
	ngOnInit() {
		// Init config
		this.config = this.defaultConfig = {
			title: '',
			message: '',
			icon: 'info',
			type: NotifyOrConfirmType.NOTIFICATION,
			buttonText: 'ok',
			buttons: {
				confirmButtonText: 'confirm',
				cancelButtonText: 'cancel'
			}
		};

		// On language change
		this.languageService.language$.subscribe(() => {
			this.config = this.defaultConfig = {
				title: '',
				message: '',
				icon: 'info',
				type: NotifyOrConfirmType.NOTIFICATION,
				buttonText: this.translateService.instant('notificationOrConfirm.ok'),
				buttons: {
					confirmButtonText: this.translateService.instant('notificationOrConfirm.confirm'),
					cancelButtonText: this.translateService.instant('notificationOrConfirm.cancel')
				}
			};
		});
	}

	/**
	 * Lifecycle hook AfterViewInit
	 */
	ngAfterViewInit(): void {
		// Watch for new modal call
		this.notifyOrConfirmService.modalObserver.subscribe((config: NotifyOrConfirmInterface) => {
			// Close old modal
			if (this.modal) {
				this.modal.close();
			}

			// If config set, open new modal
			if (!_.isEmpty(config)) {
				// Get configuration for new modal
				this.configFields.forEach((property) => {
					if (typeof config[property] === 'object') {
						this.config[property] = Object.assign(this.config, this.defaultConfig[property], config[property]);
					} else if (config.hasOwnProperty(property)) {
						this.config[property] = config[property];
					}
				});

				// Set icon
				if (!config.icon) {
					switch (this.config.type) {
						case NotifyOrConfirmType.CONFIRMATION:
							this.config.icon = 'assets/images/info.png';
							break;
						case NotifyOrConfirmType.NOTIFICATION:
							this.config.icon = 'assets/images/info.png';
							break;
						case NotifyOrConfirmType.ERROR:
							this.config.icon = 'assets/images/warning.png';
							break;
						case NotifyOrConfirmType.SUCCESS:
							this.config.icon = 'done';
							break;
					}
				}

				// Open new modal
				this.ngZone.run(() => {
					this.modal = this.modalService.open(this.modalElementRef, {
						windowClass: 'modalview-confirm d-flex align-items-center',
						size: 'lg',
						backdrop: this.config.type !== NotifyOrConfirmType.ERROR
					});

					this.modal.result.then(
						() => {
							this.notifyOrConfirmService.sendValue(true);
						},
						() => {
							this.notifyOrConfirmService.sendValue(false);
						}
					);
				});
			}
		});
	}
}
